import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CalendarCheck, Printer, CalendarDays, List, CalendarPlus } from "lucide-react";

// Dealer's operating time zone — every date/time on this page is rendered in
// this tz so an iPad in California, a browser in Kansas City, and the print
// output all agree. Defaults to America/Chicago (dealer's home tz); pulled
// from settings.quiet_hours_timezone when available.
const DEFAULT_TZ = "America/Chicago";

function formatWhen(iso, tz = DEFAULT_TZ) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
}

function formatTimeOnly(iso, tz = DEFAULT_TZ) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
}

// Compare two ISO dates in the dealer's local time zone (not the browser's).
// Two UTC timestamps that fall on the same Chicago calendar day should count
// as the same "day" even if the browser is running in another tz.
function sameLocalDay(iso, target, tz = DEFAULT_TZ) {
  if (!iso || !target) return false;
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return fmt.format(new Date(iso)) === fmt.format(target);
  } catch {
    return false;
  }
}

export default function AppointmentsPage() {
  const [appts, setAppts] = useState([]);
  const [advisors, setAdvisors] = useState({});
  const [view, setView] = useState("list"); // 'list' | 'calendar'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/appointments"),
      api.get("/advisors"),
      api.get("/declines"),
      api.get("/settings").catch(() => ({ data: null })),
    ])
      .then(([a, adv, dcs, setts]) => {
        setAppts(a.data);
        const advMap = {};
        adv.data.forEach((x) => (advMap[x.id] = { name: x.name, color: x.color || "#0f2b6b" }));
        const declineToAdvisor = {};
        dcs.data.forEach((d) => {
          const rec = advMap[d.advisor_id];
          declineToAdvisor[d.id] = rec || { name: "—", color: "#9ca3af" };
        });
        setAdvisors(declineToAdvisor);
        setSettings(setts.data);
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, []);

  // Fall back to the dealer's home tz for pre-settings loads; once settings
  // arrive, every render below re-computes with the real value.
  const tz = settings?.quiet_hours_timezone || DEFAULT_TZ;
  const stage1Days = settings?.stage_1_days ?? 7;
  const stage1Disc = settings?.stage_1_discount ?? 30;
  const stage2Days = settings?.stage_2_days ?? 15;
  const stage2Disc = settings?.stage_2_discount ?? 40;

  // Split into Upcoming (today forward · soonest first) and Past (most
  // recent first). "Confirmed" = status "scheduled"; "Pending" = "pending".
  const nowMs = Date.now();
  const upcoming = useMemo(
    () => [...appts]
      .filter((a) => a.status !== "closed" && new Date(a.scheduled_for).getTime() >= nowMs - 60 * 60 * 1000)
      .sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for)),
    [appts, nowMs]
  );
  const pastAppts = useMemo(
    () => [...appts]
      .filter((a) => a.status === "closed" || new Date(a.scheduled_for).getTime() < nowMs - 60 * 60 * 1000)
      .sort((a, b) => new Date(b.scheduled_for) - new Date(a.scheduled_for)),
    [appts, nowMs]
  );

  function estimateFor(a) {
    if (a.ro_total && a.status === "closed") return a.ro_total;
    return (a.service_items || []).reduce((s, x) => s + (x.price || 0), 0);
  }
  function statusBadge(a) {
    if (a.status === "closed") return { label: "Paid", cls: "bg-emerald-600 text-white" };
    if (a.status === "pending") return { label: "Pending", cls: "bg-amber-500 text-white" };
    if (new Date(a.scheduled_for).getTime() < nowMs - 60 * 60 * 1000)
      return { label: "Past", cls: "bg-neutral-400 text-white" };
    return { label: "Confirmed", cls: "bg-[hsl(var(--gold))] text-neutral-900" };
  }

  const downloadIcs = async (scope) => {
    try {
      const res = await api.get(`/appointments/ics?scope=${scope}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      // iOS Safari ignores the `download` attribute on blob URLs — tapping
      // does nothing. Open in a new tab instead so Safari can hand the .ics
      // off to the Calendar app via the share sheet.
      const ua = navigator.userAgent;
      const isIOS =
        /iPad|iPhone|iPod/.test(ua) ||
        (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);

      if (isIOS) {
        const opened = window.open(url, "_blank");
        if (!opened) {
          // Popup blocked — fall back to in-place nav
          window.location.href = url;
        }
        toast.success(
          scope === "today"
            ? "Today's schedule opened — tap Share ↑ then \"Add to Calendar\" to import."
            : "Confirmed appointments opened — tap Share ↑ then \"Add to Calendar\" to import.",
          { duration: 6000 }
        );
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `appointments-${scope}.ics`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(
          scope === "today"
            ? "Today's schedule downloaded — tap it to add to Calendar."
            : "Confirmed appointments downloaded — tap the file to add to Calendar."
        );
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not export calendar.");
    }
  };

  const bookedDates = useMemo(
    () => appts.map((a) => new Date(a.scheduled_for)),
    [appts]
  );

  const forSelectedDate = useMemo(
    () =>
      appts
        .filter((a) => sameLocalDay(a.scheduled_for, selectedDate, tz))
        .sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for)),
    [appts, selectedDate, tz]
  );

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Scheduled Appointments" />
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-label text-muted-foreground">Bookings</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Scheduled Appointments</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Every appointment a customer booked through the saved offer schedule —
            {" "}{stage1Days} days at {stage1Disc}% off, then {stage2Days} days at {stage2Disc}% off.
            &quot;Follow-Up &amp; CSI&quot; is the history log — appointments already visited or closed out.
            Use it for the follow-up call after service and CSI check-ins.
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <div className="flex border border-border rounded-md p-0.5">
            <Button
              size="sm"
              variant={view === "list" ? "default" : "ghost"}
              onClick={() => setView("list")}
              data-testid="view-list-btn"
              className="h-8"
            >
              <List className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              List
            </Button>
            <Button
              size="sm"
              variant={view === "calendar" ? "default" : "ghost"}
              onClick={() => setView("calendar")}
              data-testid="view-calendar-btn"
              className="h-8"
            >
              <CalendarDays className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Calendar
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={() => downloadIcs("today")}
            data-testid="ics-today-btn"
            title="Adds today's appointments to your iPhone Calendar"
          >
            <CalendarPlus className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Today → iPhone
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadIcs("upcoming")}
            data-testid="ics-upcoming-btn"
            title="Adds every confirmed appointment to your iPhone Calendar"
          >
            <CalendarPlus className="w-4 h-4 mr-2" strokeWidth={1.75} />
            All Confirmed → iPhone
          </Button>
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-appointments-btn"
          >
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6 max-w-2xl">
        <Card className="p-5 border-border" data-testid="appt-kpi-total">
          <div className="font-mono-label text-muted-foreground">Total</div>
          <div className="font-display text-3xl font-bold tracking-tighter mt-2">{appts.length}</div>
        </Card>
        <Card className="p-5 border-border" data-testid="appt-kpi-upcoming">
          <div className="font-mono-label text-muted-foreground">Confirmed</div>
          <div className="font-display text-3xl font-bold tracking-tighter mt-2 text-emerald-600">{upcoming.length}</div>
        </Card>
        <Card className="p-5 border-border" data-testid="appt-kpi-past">
          <div className="font-mono-label text-muted-foreground">Past · History</div>
          <div className="font-display text-3xl font-bold tracking-tighter mt-2 text-muted-foreground">{pastAppts.length}</div>
        </Card>
      </div>

      {view === "calendar" ? (
        <div className="grid md:grid-cols-2 gap-6" data-testid="calendar-view">
          <Card className="p-4 border-border">
            <div className="font-mono-label text-muted-foreground mb-3">Month view</div>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ booked: bookedDates }}
              modifiersClassNames={{
                booked:
                  "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-[hsl(var(--gold))]",
              }}
              className="rounded-md"
            />
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold))]" />
              Day with booked appointments
            </div>
          </Card>

          <Card className="p-5 border-border">
            <div className="font-mono-label text-muted-foreground">
              {selectedDate.toLocaleDateString([], {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
            <div className="font-display text-xl font-bold tracking-tight mt-1 mb-4">
              {forSelectedDate.length} appointment{forSelectedDate.length === 1 ? "" : "s"}
            </div>
            {forSelectedDate.length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Nothing booked this day yet.
              </div>
            )}
            <div className="space-y-3">
              {forSelectedDate.map((a) => (
                <div
                  key={a.id}
                  className="border border-border rounded-md p-3 hover:bg-muted/40 transition-colors"
                  data-testid={`cal-appt-${a.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold">
                      {formatTimeOnly(a.scheduled_for, tz)}
                    </div>
                    <Badge className="bg-[hsl(var(--gold))] text-neutral-900 font-semibold">
                      {settings?.stage_2_discount ?? a.discount ?? 40}% OFF
                    </Badge>
                  </div>
                  <div className="mt-2 font-medium">{a.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{a.customer_phone}</div>
                  <div className="text-sm mt-1">{a.vehicle}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {a.service_items.map((s) => s.name).join(", ")}
                  </div>
                  <div className="text-xs mt-2">
                    <span className="text-muted-foreground">Advisor: </span>
                    <span className="font-medium inline-flex items-center gap-1.5">
                      {advisors[a.decline_id] ? (
                        <>
                          <span
                            className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                            style={{ backgroundColor: advisors[a.decline_id].color }}
                          />
                          {advisors[a.decline_id].name}
                        </>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      ) : (
        <>
          {/* UPCOMING — today forward, soonest first */}
          <Card className="p-0 overflow-hidden border-border mb-6" data-testid="upcoming-appts-card">
            <div className="p-5 border-b border-border flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-emerald-600" strokeWidth={1.75} />
              <div className="font-display text-lg font-bold tracking-tight">Upcoming</div>
              <div className="text-xs text-muted-foreground ml-2">{upcoming.length} scheduled · soonest first</div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Declined Service</TableHead>
                  <TableHead className="text-right">Estimate $</TableHead>
                  <TableHead>Advisor</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcoming.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-sm text-muted-foreground">
                      Nothing on the schedule yet — new bookings show up here as customers tap the SMS link.
                    </TableCell>
                  </TableRow>
                )}
                {upcoming.map((a) => {
                  const badge = statusBadge(a);
                  return (
                    <TableRow key={a.id} className="hover:bg-muted/40" data-testid={`appt-upcoming-${a.id}`}>
                      <TableCell className="font-medium whitespace-nowrap">{formatWhen(a.scheduled_for, tz)}</TableCell>
                      <TableCell>
                        <div>{a.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{a.customer_phone}</div>
                      </TableCell>
                      <TableCell className="text-sm">{a.vehicle}</TableCell>
                      <TableCell className="text-sm max-w-[260px] truncate">
                        {(a.service_items || []).map((s) => s.name).join(", ")}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        ${Math.round(estimateFor(a)).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {advisors[a.decline_id] ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                              style={{ backgroundColor: advisors[a.decline_id].color }}
                            />
                            <span>{advisors[a.decline_id].name}</span>
                          </span>
                        ) : ("—")}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${badge.cls} font-semibold`}>{badge.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* PAST — most recent first, kept for follow-up + CSI */}
          <Card className="p-0 overflow-hidden border-border" data-testid="past-appts-card">
            <div className="p-5 border-b border-border">
              <div className="font-display text-lg font-bold tracking-tight text-muted-foreground">Past</div>
              <div className="text-xs text-muted-foreground mt-1">
                Already-visited or closed appointments — kept for follow-up calls and CSI check-ins.
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Declined Service</TableHead>
                  <TableHead className="text-right">Estimate $</TableHead>
                  <TableHead>Advisor</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pastAppts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-sm text-muted-foreground">
                      No past appointments yet.
                    </TableCell>
                  </TableRow>
                )}
                {pastAppts.slice(0, 200).map((a) => {
                  const badge = statusBadge(a);
                  return (
                    <TableRow key={a.id} className="hover:bg-muted/40 opacity-80" data-testid={`appt-past-${a.id}`}>
                      <TableCell className="whitespace-nowrap">{formatWhen(a.scheduled_for, tz)}</TableCell>
                      <TableCell>
                        <div>{a.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{a.customer_phone}</div>
                      </TableCell>
                      <TableCell className="text-sm">{a.vehicle}</TableCell>
                      <TableCell className="text-sm max-w-[260px] truncate">
                        {(a.service_items || []).map((s) => s.name).join(", ")}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        ${Math.round(estimateFor(a)).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {advisors[a.decline_id] ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                              style={{ backgroundColor: advisors[a.decline_id].color }}
                            />
                            <span>{advisors[a.decline_id].name}</span>
                          </span>
                        ) : ("—")}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${badge.cls} font-semibold`}>{badge.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </DashboardShell>
  );
}
